/*
 *  -----------------------------------------------------------------------
 *    <copyright file="manage-signatory-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Signatory } from '@interfaces/report-templates.interface';
import cn from 'clsx';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Errors } from '@components/central-monitoring/panels/add-panel/interfaces';
import Button from '@components/core/buttons';
import CharactersRemaining from '@components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle, DialogContent } from '@components/core/dialog';
import Loading from '@components/core/loading';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import TextField from '@components/core/text-field';
import { SignatoryInitialState } from '@components/shared/manage-signatories-grid/manage-signatory-dialog/manage-signatory-dialog.data';
import { ManageSignatoryDialogProps } from '@components/shared/manage-signatories-grid/manage-signatory-dialog/manage-signatory-dialog.interface';
import { useTranslation } from '@hooks/index';
import { validateFields } from '@utils/validation';

import './styles.scss';

const ManageSignatoryDialog = ({
    currentSignatory,
    onClose,
    onCreate,
    onUpdate,
    isLoading,
}: ManageSignatoryDialogProps) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors, isDirty },
    } = useForm<Signatory>({
        mode: 'all',
        defaultValues: {
            ...(currentSignatory || SignatoryInitialState),
        },
    });

    const validate = (value: string, field: string, length?: number) => {
        const error: Errors = validateFields(value, field, length);

        return error[field];
    };

    const onSubmit = () => {
        const submitData = getValues();

        if (currentSignatory && onUpdate) {
            onUpdate(submitData);
        } else {
            onCreate(submitData);
        }
    };

    return (
        <CustomDialog fullWidth maxWidth="md" open data-testid="signatory-dialog">
            <form id="signatory-dialog-form">
                <DialogTitle id="signatory-dialog-title">
                    {t(currentSignatory ? 'cm:editSignatoryRecordID' : 'cm:addSignatory', {
                        recordId: currentSignatory?._itemId ?? currentSignatory?.recordId,
                    })}

                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        data-testid="close-dialog"
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            onClose();
                        }}
                    />
                </DialogTitle>
                <DialogContent className="signatory-dialog-content">
                    {isLoading && <Loading float />}
                    <div className="type-edc-wrapper k-mt-4 !k-mb-3">
                        <div className="form-control-type mr">
                            <Controller
                                control={control}
                                name="jobTitle"
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('ra:requiredFieldError'),
                                    },
                                    validate: {
                                        maxLength: (value) => validate(value ?? '', 'jobTitle', 150) ?? undefined,
                                    },
                                }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={cn('form-control-type')}>
                                        <TextField
                                            label={`${t('reports:jobTitle')}*`}
                                            id="signatory-job-title"
                                            data-testId="signatory-job-title"
                                            fullWidth
                                            value={value}
                                            onChange={onChange}
                                            placeholder={t('admin:enterJobTitle')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="signatory-job-title-characters"
                                                    length={value?.length}
                                                    max={150}
                                                    error={error?.message}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!error?.message}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <div className="form-control-type" />
                    </div>
                    <div className="type-edc-wrapper">
                        <div className="form-control-type mr">
                            <Controller
                                control={control}
                                name="firstName"
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('ra:requiredFieldError'),
                                    },
                                    validate: {
                                        maxLength: (value) => validate(value ?? '', 'firstName', 70) ?? undefined,
                                    },
                                }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={cn('form-control-type')}>
                                        <TextField
                                            label={`${t('firstName')}*`}
                                            id="signatory-first-name"
                                            data-testId="signatory-first-name"
                                            fullWidth
                                            value={value}
                                            onChange={onChange}
                                            placeholder={t('admin:enterFirstName')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="signatory-first-name-characters"
                                                    length={value?.length}
                                                    max={70}
                                                    error={error?.message}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!error?.message}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                        <div className="form-control-type">
                            <Controller
                                control={control}
                                name="lastName"
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('ra:requiredFieldError'),
                                    },
                                    validate: {
                                        maxLength: (value) => validate(value ?? '', 'lastName', 70) ?? undefined,
                                    },
                                }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <div className={cn('form-control-type')}>
                                        <TextField
                                            label={`${t('lastName')}*`}
                                            id="signatory-last-name"
                                            data-testId="signatory-last-name"
                                            fullWidth
                                            value={value}
                                            onChange={onChange}
                                            placeholder={t('admin:enterLastName')}
                                            helperText={
                                                <CharactersRemaining
                                                    id="signatory-last-name-characters"
                                                    length={value?.length}
                                                    max={70}
                                                    error={error?.message}
                                                />
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!error?.message}
                                        />
                                    </div>
                                )}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions
                    className={cn('!k-pr-6 !k-pl-6 k-mb-6', {
                        'k-flex !k-justify-content-between': currentSignatory,
                    })}
                >
                    {currentSignatory && <ModalUpdatedInfo item={currentSignatory} isBFF />}
                    <Button
                        disabled={Object.keys(errors).length !== 0 || !isDirty || isLoading}
                        text={currentSignatory ? 'update' : 'add'}
                        id="submit-signatory"
                        data-testid="submit-signatory"
                        type="button"
                        onClick={handleSubmit(() => onSubmit())}
                    />
                </DialogActions>
            </form>
        </CustomDialog>
    );
};

export default ManageSignatoryDialog;
